import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Sparkles, UsersRound, LogOut, Plus, BarChart3, KeyRound, Package, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { BirthdayGreeting } from "@/components/BirthdayGreeting";
import { ChangePasswordDialog, ForcePasswordChange } from "@/components/PasswordDialogs";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-secondary/40">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[15px] tracking-tight">TSG AI Hub</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tai Sang Global</div>
            </div>
          </div>
        </div>

        <div className="px-3 py-4">
          <Button
            data-testid="sidebar-new-lead-button"
            onClick={() => navigate("/new")}
            className="w-full justify-start gap-2 rounded-md h-10 font-semibold"
          >
            <Plus className="h-4 w-4" /> New Lead
          </Button>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" /> {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/orders"
              data-testid="nav-orders"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`
              }
            >
              <Package className="h-4 w-4" /> Order Book
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/customer-master"
              data-testid="nav-customer-master"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`
              }
            >
              <Building2 className="h-4 w-4" /> Customer Master
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/team"
              data-testid="nav-team"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`
              }
            >
              <UsersRound className="h-4 w-4" /> Team
            </NavLink>
          )}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar name={user?.name} size={34} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize truncate">
                {user?.role === "admin" ? "Global Admin" : `Consultant · ${user?.country}`}
              </div>
            </div>
            <button
              data-testid="change-password-button"
              onClick={() => setPwOpen(true)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1.5"
              title="Change password"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="text-muted-foreground hover:text-destructive transition-colors p-1.5"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        <BirthdayGreeting />
        {children}
      </main>

      <ChangePasswordDialog open={pwOpen} setOpen={setPwOpen} />
      <ForcePasswordChange />
    </div>
  );
};
